const ACTIVE_DECISIONS = new Set(['approve', 'override', 'keep_online']);

export const WORKLIST_GROUPS = [
  { id: 'needs-review', label: 'Needs your review' },
  { id: 'ready-to-send', label: 'Ready to send' },
  { id: 'waiting', label: 'Waiting on something else' },
];

const groupFor = ({ needsReview = 0, sendable = 0 }) => {
  if (needsReview > 0) return 'needs-review';
  if (sendable > 0) return 'ready-to-send';
  return 'waiting';
};

const cachedRunCounts = (item) => {
  const proposal = item.proposal || {};
  const statuses = item.section_statuses || {};
  return (proposal.changed_sections || []).reduce((counts, section) => {
    const status = statuses[section] || 'unavailable';
    const decision = proposal.review_decisions?.[section]?.decision;
    const dependencyBlocked = status === 'dependency_blocked';
    if (!dependencyBlocked && ACTIVE_DECISIONS.has(decision)) counts.sendable += 1;
    else if (['safe', 'already_satisfied', 'conflict'].includes(status)) counts.needsReview += 1;
    else counts.blocked += 1;
    return counts;
  }, { needsReview: 0, sendable: 0, blocked: 0 });
};

const newOrderRecord = (item, context) => {
  const order = item.order || {};
  const validation = context.validations?.[order.id];
  const duplicates = validation?.duplicates?.length || 0;
  const duplicateReady = duplicates === 0 || Boolean(context.duplicateAcknowledgments?.[order.id]);
  const sendable = Boolean(validation?.submit_ready && duplicateReady);
  return {
    id: `new-order:${order.id}`,
    kind: 'new-order',
    source: item,
    title: order.internal_order_number || order.desktop_sync?.temporary_label || order.recipient_name || 'Offline order',
    detail: order.recipient_name || 'New order created on this computer',
    needsReview: sendable ? 0 : 1,
    sendable: sendable ? 1 : 0,
    blocked: 0,
  };
};

const orderChangeRecord = (item, kind, isAdmin) => {
  const mutation = item.mutation || {};
  const order = item.order || {};
  return {
    id: `${kind}:${mutation.id}`,
    kind,
    source: item,
    title: order.internal_order_number || mutation.target_id || 'Offline order change',
    detail: kind === 'deletion' ? 'Deletion request' : 'Existing order changed offline',
    needsReview: isAdmin ? 1 : 0,
    sendable: 0,
    blocked: isAdmin ? 0 : 1,
  };
};

const offlineRunRecord = (item) => {
  const run = item.run || {};
  const ready = item.review_state === 'ready_to_sync';
  const waiting = ['waiting_on_orders', 'blocked'].includes(item.review_state);
  return {
    id: `offline-run:${run.id}`,
    kind: 'offline-run',
    source: item,
    title: run.name || 'Offline run',
    detail: `${item.stop_count || 0} stop${item.stop_count === 1 ? '' : 's'} · created on this computer`,
    needsReview: ready || waiting ? 0 : 1,
    sendable: ready ? 1 : 0,
    blocked: waiting ? Math.max(1, item.blockers?.length || 0) : 0,
  };
};

const cachedRunRecord = (item) => {
  const proposal = item.proposal || {};
  const counts = cachedRunCounts(item);
  const changeCount = (proposal.changed_sections || []).length;
  return {
    id: `cached-run:${proposal.cloud_run_id}`,
    kind: 'cached-run',
    source: item,
    title: proposal.baseline?.name || proposal.desired?.name || 'Cached run',
    detail: `${changeCount} change${changeCount === 1 ? '' : 's'}`,
    ...counts,
  };
};

export const buildWorklistRecords = ({
  stage,
  newOrders = [],
  orderConflicts = [],
  deletionRequests = [],
  offlineRuns = [],
  cachedRuns = [],
  validations = {},
  duplicateAcknowledgments = {},
  isAdmin = true,
}) => {
  let records = [];
  if (stage === 'new-orders') {
    records = newOrders.map(item => newOrderRecord(item, { validations, duplicateAcknowledgments }));
  } else if (stage === 'order-changes') {
    records = [
      ...deletionRequests.map(item => orderChangeRecord(item, 'deletion', isAdmin)),
      ...orderConflicts.map(item => orderChangeRecord(item, 'order-change', isAdmin)),
    ];
  } else if (stage === 'runs') {
    records = [
      ...offlineRuns.map(offlineRunRecord),
      ...cachedRuns.map(cachedRunRecord),
    ];
  }
  return records.map(record => ({
    ...record,
    group: groupFor(record),
  }));
};

export const groupWorklistRecords = (records) => Object.fromEntries(
  WORKLIST_GROUPS.map(group => [group.id, records.filter(record => record.group === group.id)]),
);
